import type { ReactNode } from 'react';
import { Pin } from 'lucide-react';
import { CaretDownIcon, CheckIcon, SparkleIcon } from '@phosphor-icons/react';
import { fmtMoneyWhole } from '../flight_format.js';
import type { FlightOfferSource } from '../flight_offer_source.js';
import { FlightLegDense, flightNumberLabel } from './flight_leg.js';
import { CAROUSEL_SLIDE } from './offer_carousel.js';
import { FlightOfferCardStack } from './offer_card_stack.js';
import type { FlightAirportSwitchView, FlightOfferCopy, FlightOfferPlaces } from './offer_copy.js';
import { AirportSwitchTag, AxisFlag, PolicyFlag } from './offer_flags.js';
import type { OfferLayout } from './use_offer_layout.js';
import './flight_offers.css';

/** O estado do toggle "Fixar" de UMA linha (#2294): fixada? + o gesto. Local ao
 *  arquivo — o contrato PÚBLICO do pino é o `FlightPinControl` (offer_list). */
export interface OfferPin {
  readonly pinned: boolean;
  readonly onToggle: () => void;
}

/**
 * O toggle "Fixar" da linha (#2294) — mora no rodapé `.fq-why` ao lado de
 * "Detalhes", nunca nos selos da borda (que os cantos já usam: eixo à esquerda,
 * política à direita). O texto visível ("Fixar"/"Fixado") É o nome acessível
 * (`aria-pressed` carrega o estado), então o pino é decorativo (`aria-hidden`).
 * `stopPropagation`: a linha inteira é o toggle das tarifas — fixar não pode
 * abrir a escada junto.
 */
function PinToggle({ pinned, onToggle, copy }: OfferPin & { readonly copy: FlightOfferCopy }) {
  return (
    <button
      type="button"
      className={`fq-pin${pinned ? ' is-pinned' : ''}`}
      aria-pressed={pinned}
      onClick={(e) => {
        e.stopPropagation();
        onToggle();
      }}
    >
      <Pin size={13} fill={pinned ? 'currentColor' : 'none'} aria-hidden />
      {pinned ? copy.pinOn : copy.pinOff}
    </button>
  );
}

/**
 * As ações do rodapé, na ordem `Detalhes · Fixar` (#2363 + #2294).
 *
 * Componente próprio porque é o que elas já eram conceitualmente, e porque
 * extraí-las devolve a `FlightOfferRow` ao teto de complexidade: as guardas
 * (`onDetail`/`pin` presentes) eram outros tantos ramos numa função que já
 * orquestra pernas, nota, trilho e expansão.
 *
 * Já houve um `Assentos` aqui (#2363), segunda porta do MESMO drawer de
 * "Detalhes" com foco no mapa. Saiu em #2514: dois links lado a lado abrindo o
 * mesmo destino liam como duas coisas, e o mapa continua acessível de dentro do
 * drawer.
 *
 * `stopPropagation` em todas: a linha inteira é alvo de clique, e nenhuma dessas
 * pode escolher a oferta de carona.
 */
function OfferActions<O extends FlightOfferSource>({
  offer,
  position,
  onDetail,
  pin,
  copy,
}: {
  readonly offer: O;
  readonly position: number;
  readonly onDetail?: ((offer: O, position: number) => void) | undefined;
  readonly pin?: OfferPin | undefined;
  readonly copy: FlightOfferCopy;
}) {
  if (!onDetail && !pin) return null;
  return (
    <span className="fq-actions">
      {onDetail && (
        <button
          type="button"
          className="fq-detail"
          onClick={(e) => {
            e.stopPropagation();
            onDetail(offer, position);
          }}
        >
          {copy.leg.viewDetail}
        </button>
      )}
      {pin && <PinToggle pinned={pin.pinned} onToggle={pin.onToggle} copy={copy} />}
    </span>
  );
}

function OfferMain<O extends FlightOfferSource>({
  offer,
  chosen,
  position,
  onDetail,
  pin,
  airportSwitch,
  copy,
  locale,
  places,
}: {
  readonly offer: O;
  readonly chosen: boolean;
  readonly position: number;
  readonly onDetail?: ((offer: O, position: number) => void) | undefined;
  /** O toggle "Fixar" (#2294), quando o host o arma. */
  readonly pin?: OfferPin | undefined;
  readonly airportSwitch?: FlightAirportSwitchView | null | undefined;
  readonly copy: FlightOfferCopy;
  readonly locale: string;
  readonly places: FlightOfferPlaces;
}) {
  const segment = offer.segments[0];
  const returnSegment = offer.returnLeg?.segments[0];
  const leg = { copy: copy.leg, locale, cityOf: places.cityOf };
  const stops = offer.stops ?? offer.segments.length - 1;
  const flightNumber = flightNumberLabel(segment?.carrier, segment?.flightNumber);
  return (
    <div className="fq-main">
      {chosen && (
        <span className="fq-chosen">
          <CheckIcon size={12} aria-hidden />
          {copy.chosen}
        </span>
      )}
      <div className={`fq-legs${offer.returnLeg ? '' : ' is-single'}`}>
        {segment &&
          (offer.returnLeg && returnSegment ? (
            <>
              <FlightLegDense seg={segment} stops={stops} kind="outbound" {...leg} />
              <FlightLegDense
                seg={returnSegment}
                stops={offer.returnLeg.stops ?? offer.returnLeg.segments.length - 1}
                kind="return"
                {...leg}
              />
            </>
          ) : (
            <FlightLegDense seg={segment} stops={stops} flightNumber={flightNumber} {...leg} />
          ))}
      </div>
      <AirportSwitchTag found={airportSwitch} copy={copy} />
      {(offer.why || onDetail || pin) && (
        <div className="fq-why">
          {offer.why && (
            <span className="fq-why-text">
              <SparkleIcon size={12} weight="fill" aria-hidden />
              {offer.why}
            </span>
          )}
          <OfferActions
            offer={offer}
            position={position}
            onDetail={onDetail}
            pin={pin}
            copy={copy}
          />
        </div>
      )}
    </div>
  );
}

/**
 * Linha de um itinerário no card "Melhores voos" (handoff flight quote card).
 * Layout único para one-way e round-trip: as pernas ocupam a largura do card
 * via `.fq-legs` (uma `FlightLegDense` por perna — head denso com data ·
 * condição · duração · logo · nº do voo, e trecho de uma linha), o preço +
 * "Ver tarifas" à direita (`.fq-price-zone`), e a
 * nota do agente (`why`) num rodapé full-width abaixo das pernas (`.fq-why`).
 * O badge de recomendação e a ressalva de política são os dois selos
 * (`.fq-flag`) que interrompem a borda do topo, em cantos opostos — o de
 * política só existe para voo FORA da política, e é a porta do drawer com o
 * motivo.
 *
 * Round-trip (`offer.returnLeg`): duas pernas ida | volta lado a lado, cada uma
 * com o `kind` que lhe dá o glifo e a palavra VISÍVEL (#2363 — a palavra recolhe
 * para sr-only só na faixa apertada de duas colunas; ver `FlightLegDense`).
 * One-way: perna única (`.fq-legs.is-single`), sem `kind` e sem grupo (não há
 * sentido a distinguir), com os extras do one-way (nº do voo + "Detalhes") —
 * assim um card de 1 trecho e uma perna do combo ficam idênticos.
 *
 * Componente compartilhado entre a lista (`FlightOffersList`) e o drawer de
 * filtro (`FlightFilterDrawer`) — fonte única.
 */
export function FlightOfferRow<O extends FlightOfferSource>(props: FlightOfferRowProps<O>) {
  // O desenho do celular (#3874) é outra ÁRVORE, não outro estilo — ver
  // `use_offer_layout`. A linha continua sendo a porta única dos três hosts;
  // quem mede o container é a lista.
  if (props.layout === 'stack') return <FlightOfferCardStack {...props} />;
  return <FlightOfferRowWide {...props} />;
}

/** `O` é a oferta do host: os callbacks a devolvem como veio. */
export interface FlightOfferRowProps<O extends FlightOfferSource = FlightOfferSource> {
  /** Qual desenho: `wide` (padrão, a linha desktop) ou `stack` (o card do
   *  celular). Decidido pela largura do CONTAINER da lista, nunca pela janela. */
  readonly layout?: OfferLayout | undefined;
  /** O card é um slide do carrossel do celular (#3874). */
  readonly slide?: boolean | undefined;
  /** Em que superfície o card do celular está — só o `stack` lê; a linha
   *  desktop é uma só. Muda o cabeçalho da perna ÚNICA e a gramática do toque:
   *  - `chat`: "Voo · Sex, 25 set" — o card do chat e a gaveta "ver todos", onde
   *    o card é a única coisa em tela que diz o dia; a faixa de preço leva o
   *    caret (promete a tela de tarifas);
   *  - `itinerary` (padrão): "LATAM · LA 3050, LA 3051" — o roteiro
   *    multi-trecho, com a data no cabeçalho do trecho; faixa com caret;
   *  - `results`: a lista do resultado determinístico (#3893) — cabeçalho da
   *    companhia, corpo com `py-5`, faixa SEM caret e o card INTEIRO como alvo
   *    (tocar seleciona o trecho; a data está no título da página).
   *  Ida e volta seguem com "Ida/Volta · data" nas três. */
  readonly variant?: 'chat' | 'itinerary' | 'results' | undefined;
  readonly offer: O;
  readonly position: number;
  readonly copy: FlightOfferCopy;
  /** O idioma das datas. */
  readonly locale: string;
  readonly places: FlightOfferPlaces;
  /** Expansão inline: a linha inteira vira toggle das tarifas. */
  readonly onToggleFares?: ((offer: O) => void) | undefined;
  readonly expanded?: boolean | undefined;
  /** Conteúdo da expansão renderizado DENTRO do <li> (abaixo da linha). */
  readonly expansion?: ReactNode;
  /** O preço da linha é o do PAR ida+volta mesmo sem `returnLeg` na oferta —
   *  as fases do RT no explorer mostram a célula da matriz por perna. Muda só a
   *  LEGENDA do preço ("ida e volta, por pessoa"). */
  readonly pairPricing?: boolean | undefined;
  /** Rótulo do toggle da escada — ver `RailCta`. */
  readonly faresCtaLabel?: string | undefined;
  readonly onOpenPolicy?: ((offer: O) => void) | undefined;
  /** "Detalhes" — abre o drawer "Detalhe do voo" (linha do tempo por perna). */
  readonly onDetail?: ((offer: O, position: number) => void) | undefined;
  /** Eco do servidor: ESTA é a oferta que fechou a viagem. Marca de LEITURA — o
   *  card que a traz nasce congelado (policy `frozenWhen`), então ela nunca
   *  convive com um CTA armado. */
  readonly chosen?: boolean | undefined;
  /** Toggle "Fixar" da tela de pesquisa (#2294). Ausente ⇒ nenhuma linha ganha
   *  pino (os cards do chat e o card multi não passam) — a fixação é só da
   *  bancada, injetada pelo host via prop (o kit não conhece o store). */
  readonly pin?: OfferPin | undefined;
  /** A volta parte de outro aeroporto que o da chegada da ida (#4203) — só a
   *  fase 2 do ida e volta da bancada passa. */
  readonly airportSwitch?: FlightAirportSwitchView | null | undefined;
}

function FlightOfferRowWide<O extends FlightOfferSource>({
  slide,
  offer,
  position,
  onToggleFares,
  expanded,
  expansion,
  pairPricing,
  faresCtaLabel,
  onOpenPolicy,
  onDetail,
  chosen,
  pin,
  airportSwitch,
  copy,
  locale,
  places,
}: FlightOfferRowProps<O>) {
  const isBest = offer.badges?.some((b) => b.kind === 'best') ?? false;
  const topBadge = offer.badges?.[0];
  const out = offer.policy?.status === 'out';

  const priceZone = (
    <OfferRail
      offer={offer}
      position={position}
      expanded={expanded}
      pairPricing={pairPricing}
      faresCtaLabel={faresCtaLabel}
      onToggleFares={onToggleFares}
      copy={copy}
    />
  );

  // A expansão vive dentro da linha (que é toggle); cliques nos seus
  // controles (radios/CTA/retry) NÃO devem borbulhar pro toggle e fechar a
  // expansão.
  const expansionBlock = expansion && (
    <div
      className="fq-expansion"
      onClick={(e) => {
        e.stopPropagation();
      }}
      role="presentation"
    >
      {expansion}
    </div>
  );

  // Fonte única: uma linha por perna via `FlightLegDense` (`shared/ui/flight_leg`) —
  // irmã da `FlightLeg` que o resumo da reserva e o bilhete desenham, com o head
  // denso (condição + duração ao lado da data) e sem rótulo de sentido. Round-trip
  // = ida|volta lado a lado; one-way = perna única (`is-single`), com os extras do
  // one-way (nº do voo + "Detalhes") que o combo omite.
  // A nota `why` vai SEMPRE para o rodapé full-width `.fq-why` e o badge SEMPRE
  // para o selo da borda, então o card de 1 trecho e uma perna do combo ficam
  // idênticos (header/duração/preço).
  return (
    <li
      className={slide ? CAROUSEL_SLIDE : undefined}
      // Âncora de rolagem: a faixa de curadoria do explorer traz esta linha à vista
      // pelo id da oferta. Atributo de DADO, não de estilo — os três hosts da lista
      // (card binário, card multi, explorer) o carregam de graça.
      data-offer-id={offer.offerId}
    >
      <div
        className={rowClass(isBest, out, chosen ?? false)}
        // A linha inteira é clicável (conveniência de mouse); o "Ver tarifas"/
        // "Selecionar" (sempre presente quando onToggleFares existe) é o
        // controle acessível por teclado. O clique mora numa superfície
        // apresentacional DENTRO do `<li>`, e não no item (que não é elemento
        // interativo); não é role=button p/ não aninhar semântica de botão com
        // os botões internos (shield/CTA).
        role="presentation"
        onClick={
          onToggleFares
            ? () => {
                onToggleFares(offer);
              }
            : undefined
        }
      >
        {/* O selo interrompe a borda no canto superior esquerdo em vez de ocupar
          uma linha do corpo — a mesma gramática que o selo do degrau usa um
          nível abaixo. */}
        {topBadge && <AxisFlag badge={topBadge} copy={copy} />}
        {out && <PolicyFlag offer={offer} copy={copy} onOpenPolicy={onOpenPolicy} />}
        {/* **Coluna esquerda: as pernas E a nota do agente.** A nota vivia numa
          faixa full-width abaixo de tudo, com régua horizontal própria — e isso
          cortava o card em dois no lugar errado: a divisória do trilho de decisão
          é VERTICAL e tem que descer por trás da nota, porque a nota fala do
          itinerário, não do preço. Aqui as duas viram uma coluna só, e o trilho
          (`align-self: stretch`) acompanha a altura dela. */}
        <OfferMain
          offer={offer}
          chosen={chosen ?? false}
          position={position}
          onDetail={onDetail}
          pin={pin}
          airportSwitch={airportSwitch}
          copy={copy}
          locale={locale}
          places={places}
        />
        {priceZone}
        {expansionBlock}
      </div>
    </li>
  );
}

/** A porta do trilho. Na lista principal abre/fecha as tarifas (com o caret
 *  girando); no drawer de filtro ela SELECIONA o voo — mesmo botão, porque é o
 *  único controle focável por teclado equivalente ao clique na linha. */
function RailCta<O extends FlightOfferSource>({
  offer,
  position,
  expanded,
  faresCtaLabel,
  onToggleFares,
  copy,
}: {
  readonly offer: O;
  readonly position: number;
  readonly expanded?: boolean | undefined;
  /** Rótulo do toggle da escada. Ausente ⇒ `Ver tarifas`, que é o card do chat:
   *  lá a escada é revelação de conteúdo DENTRO do card e o rodapé dela diz
   *  "Adicionar à viagem". No explorer a escada é um PASSO da jornada e o convite
   *  é `Selecionar` — o mesmo rótulo do ramo select-mode, de propósito: nas duas
   *  fases do round trip a linha promete a mesma coisa (escolher este voo), e o
   *  que muda é só se a tarifa vem depois. */
  readonly faresCtaLabel?: string | undefined;
  readonly onToggleFares: (offer: O) => void;
  readonly copy: FlightOfferCopy;
}) {
  const toggles = expanded !== undefined;
  return (
    <button
      type="button"
      className="fq-fares-cta"
      {...(toggles
        ? { 'aria-expanded': expanded }
        : { 'aria-label': copy.selectOfferAria(position) })}
      onClick={(e) => {
        e.stopPropagation();
        onToggleFares(offer);
      }}
    >
      {toggles ? (
        <>
          {expanded ? copy.closeFares : (faresCtaLabel ?? copy.viewFares)}
          {/* O caret é da gramática de REVELAÇÃO ("Ver tarifas" abre um painel
              abaixo). Com o rótulo de escolha ele prometeria um acordeão onde há
              um passo, e o frame 445-32443 não o mostra. Aberto, o botão FECHA e
              o caret volta — aí ele descreve o gesto de novo. */}
          {(expanded || !faresCtaLabel) && (
            <CaretDownIcon size={14} className={expanded ? 'is-open' : undefined} aria-hidden />
          )}
        </>
      ) : (
        copy.selectOffer
      )}
    </button>
  );
}

/**
 * **Trilho de decisão** — a coluna da direita: preço e a porta das tarifas (a
 * ressalva de política subiu para o selo da borda, fora do trilho). Componente
 * próprio porque é o que ele já era conceitualmente, e porque extraí-lo devolve
 * a `FlightOfferRow` ao teto de complexidade (ela orquestra pernas + nota +
 * trilho + expansão).
 */
function OfferRail<O extends FlightOfferSource>({
  offer,
  position,
  expanded,
  pairPricing,
  faresCtaLabel,
  onToggleFares,
  copy,
}: {
  readonly offer: O;
  readonly position: number;
  readonly expanded?: boolean | undefined;
  readonly pairPricing?: boolean | undefined;
  readonly faresCtaLabel?: string | undefined;
  readonly onToggleFares?: ((offer: O) => void) | undefined;
  readonly copy: FlightOfferCopy;
}) {
  return (
    <div className="fq-price-zone">
      {/* O trilho é alinhado à ESQUERDA, sem linha própria: a linha `justify-end`
          existia só pra o escudo caber ao lado do número, e empurrava preço e
          legenda pra margem direita. A LEGENDA vem antes do valor — "Ida e volta
          por / R$ 1.746" —, a mesma ordem da faixa de preço do card do celular
          (`PriceStrip`): a linha lê o que o número cobre antes do número. */}
      <span className="fq-price-cap">
        {/* **Uma frase fechada, não uma soma de fragmentos.** A legenda diz o que
            o número cobre, e o que ela cobre muda com o tipo de viagem — nada
            mais. A versão anterior montava `Por pessoa · a partir · ida+volta` de
            três chaves, o que trazia a ordem errada e um separador que o desenho
            não usa. `pairPricing` é a forma da VIAGEM (fases do RT no explorer):
            as linhas das pernas não carregam `returnLeg`, mas o número é o PAR
            (célula da matriz) — perguntar só à oferta legendava R$ 706 do par
            como "só ida". */}
        {(offer.returnLeg ?? pairPricing) ? copy.baseRoundTrip : copy.baseOneWay}
      </span>
      <span className="fq-price-v">{fmtMoneyWhole(offer.price)}</span>
      {onToggleFares && (
        <RailCta
          offer={offer}
          position={position}
          expanded={expanded}
          faresCtaLabel={faresCtaLabel}
          onToggleFares={onToggleFares}
          copy={copy}
        />
      )}
    </div>
  );
}

function rowClass(isBest: boolean, out: boolean, chosen: boolean): string {
  return `fq-row${isBest ? ' is-best' : ''}${out ? ' is-out' : ''}${chosen ? ' is-chosen' : ''}`;
}
